export const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

const cache = {};
function fallbackPlay(name) {
  try {
    let a = cache[name];
    if (!a) {
      a = new Audio(`/sounds/${name}.wav`);
      a.preload = 'auto';
      a.volume = 0.5;
      cache[name] = a;
    }
    a.currentTime = 0;
    a.play().catch(() => { /* autoplay blocked before first interaction */ });
  } catch { /* ignore */ }
}

// Web Audio API : fiable sur TV (webOS/Tizen) où les éléments <audio> HTML
// échouent quand le pipeline média est occupé par les vidéos.
let audioCtx = null;
const audioBuffers = {};
function ensureCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) {
    try { audioCtx = new AC(); } catch { return null; }
  }
  if (audioCtx.state === 'suspended') { try { audioCtx.resume(); } catch { /* ignore */ } }
  return audioCtx;
}
function playBuffer(c, buf) {
  const s = c.createBufferSource();
  const g = c.createGain();
  g.gain.value = 0.5;
  s.buffer = buf;
  s.connect(g);
  g.connect(c.destination);
  s.start(0);
}
function play(name) {
  try {
    const c = ensureCtx();
    if (!c) { fallbackPlay(name); return; }
    if (audioBuffers[name]) { playBuffer(c, audioBuffers[name]); return; }
    if (audioBuffers[name] === false) return; // décodage en cours
    audioBuffers[name] = false;
    fetch(`/sounds/${name}.wav`)
      .then(r => r.arrayBuffer())
      .then(arr => new Promise((ok, err) => c.decodeAudioData(arr, ok, err)))
      .then(buf => { audioBuffers[name] = buf; playBuffer(c, buf); })
      .catch(() => { delete audioBuffers[name]; fallbackPlay(name); });
  } catch { fallbackPlay(name); }
}
export const popNav = () => play('pop1');
export const popOk = () => play('pop2');
export const popKey = () => play('pop3');

// Image preloader (browser cache warm-up) — instant portrait->landscape swaps and banner rotations
const preloadedImgs = new Set();
export const preloadImg = (url) => {
  if (!url || preloadedImgs.has(url)) return;
  preloadedImgs.add(url);
  const i = new Image();
  i.src = url;
};

export const KID_RATINGS = ['Tout public', '7+', '10+'];
export const isKidProfile = p => p?.kind === 'kids' || KID_RATINGS.includes(p?.max_age_rating);

/* Quitte l'app TV selon la plateforme (Tizen / webOS / wrapper Android TV / navigateur) */
export const TV_BUILD = 'v16.06-B';
// Wrapper Fire TV (APK Amazon) : politique Amazon = aucune mention d'achat/site de paiement
export const isFireTvWrapper = typeof navigator !== 'undefined' && /MayaPlusFireTV/i.test(navigator.userAgent || '');
export const isAndroidTvWrapper = typeof navigator !== 'undefined' && /MayaPlusAndroidTV/i.test(navigator.userAgent || '');
// Wrappers natifs Android (Google TV + Fire TV) : sortie via bouton Retour + spécificités WebView
export const isNativeTvWrapper = isFireTvWrapper || isAndroidTvWrapper;
// TV Hisense VIDAA OS : app HTML5, sortie via window.close()
export const isVidaa = typeof navigator !== 'undefined' && /VIDAA|Hisense/i.test(navigator.userAgent || '');
// Plateformes où Retour à la racine propose de quitter l'app
export const canExitViaBack = isNativeTvWrapper || isVidaa;

// ── Journal de débogage à distance (TV uniquement) ──────────────────────────
// XMLHttpRequest : les vieux moteurs webOS n'ont pas fetch
const _dbgQueue = [];
let _dbgTimer = null;
export function logTv(evt, data) {
  try {
    if (!sessionStorage.getItem('tvMode')) return;
    _dbgQueue.push({ evt, data: typeof data === 'string' ? data : JSON.stringify(data || ''), at: window.location.pathname });
    if (!_dbgTimer) {
      _dbgTimer = setTimeout(() => {
        _dbgTimer = null;
        const events = _dbgQueue.splice(0, 50);
        if (!events.length) return;
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/tv-debug/log`, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(JSON.stringify({ events }));
        } catch { /* ignore */ }
      }, 2500);
    }
  } catch { /* ignore */ }
}
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => logTv('js_error', `${e.message} @${(e.filename || '').slice(-40)}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => logTv('promise_rejection', String(e.reason).slice(0, 300)));
}

export function exitTvApp() {
  try {
    if (window.tizen?.application) { window.tizen.application.getCurrentApplication().exit(); return; }
    if (window.webOSSystem?.platformBack) { window.webOSSystem.platformBack(); return; }
    if (window.webOS?.platformBack) { window.webOS.platformBack(); return; }
    if (window.ReactNativeWebView?.postMessage) { window.ReactNativeWebView.postMessage('EXIT_APP'); return; }
    window.close();
    setTimeout(() => { window.location.href = '/'; }, 250);
  } catch {
    window.location.href = '/';
  }
}
export const isKidContent = c => !!c && (c.type === 'jeunesse' || c.type === 'game'
  || (c.categories || []).includes('jeunesse') || c.category === 'jeunesse');

export const isNouveaute = c => !!c && ((c.categories || []).includes('nouveautes') || c.category === 'nouveautes');
export const isKidBanner = c => !!c && c.type !== 'game' && (c.type === 'jeunesse'
  || (c.categories || []).includes('jeunesse') || c.category === 'jeunesse');
// Round-robin interleave by content type (keeps input order within each type)
export const mixByType = (arr) => {
  const groups = {};
  arr.forEach(c => { (groups[c.type || 'autre'] = groups[c.type || 'autre'] || []).push(c); });
  const keys = shuffle(Object.keys(groups));
  const out = [];
  let added = true;
  while (added) {
    added = false;
    for (const k of keys) { const it = groups[k].shift(); if (it) { out.push(it); added = true; } }
  }
  return out;
};
export const shuffle = arr => arr
  .map(v => [Math.random(), v])
  .sort((a, b) => a[0] - b[0])
  .map(([, v]) => v);

// Images redimensionnées côté serveur pour la TV : décodage ~10x plus léger sur les CPU de TV
export const tvImg = (url, w = 480) => {
  if (!url || !/^https?:/.test(url)) return url;
  return `${process.env.REACT_APP_BACKEND_URL}/api/img?w=${w}&u=${encodeURIComponent(url)}`;
};
